import { ref, readonly, type Ref } from "vue";
import {
  adminV1OrganizationsPaymentSettingsRetrieve,
  adminV1OrganizationsPaymentSettingsCreate,
  adminV1OrganizationsPaymentSettingsUpdate,
} from "@/api/admin-payment-settings/admin-payment-settings";
import type { AdminPaymentSetting } from "@/api/model";
import { useCurrentOrganization } from "@/composables/useCurrentOrganization";

export type AdminPaymentSettingUpsertPayload = {
  id?: number;
  payabli_api_key?: string;
  payabli_api_key_expires_at?: string | null;
  payabli_entry_name?: string;
  payabli_subdomain?: string;
  payer_pays_fees?: boolean;
  allow_ach_payments?: boolean;
};

interface UsePaymentSettingsOptions {
  organizationId?: number | Ref<number>;
}

export const usePaymentSettings = (options: UsePaymentSettingsOptions = {}) => {
  const { currentOrganization } = useCurrentOrganization();

  const paymentSettings = ref<AdminPaymentSetting | null>(null);
  const isLoading = ref(false);
  const isSaving = ref(false);
  const error = ref<string | null>(null);

  const getOrgId = (): number | null => {
    if (options.organizationId) {
      return typeof options.organizationId === "number"
        ? options.organizationId
        : options.organizationId.value;
    }
    return currentOrganization.value?.id || null;
  };

  const fetchPaymentSettings = async () => {
    const orgId = getOrgId();
    if (!orgId) {
      error.value = "No organization selected";
      return;
    }

    isLoading.value = true;
    error.value = null;

    try {
      const response = await adminV1OrganizationsPaymentSettingsRetrieve(orgId);
      paymentSettings.value = response;
    } catch (err) {
      console.error("Error fetching payment settings:", err);
      error.value = "Failed to load payment settings";
      paymentSettings.value = null;
    } finally {
      isLoading.value = false;
    }
  };

  const savePaymentSettings = async (
    settings: AdminPaymentSettingUpsertPayload
  ) => {
    const orgId = getOrgId();
    if (!orgId) {
      error.value = "No organization selected";
      return;
    }

    isSaving.value = true;
    error.value = null;

    try {
      let response: AdminPaymentSetting;

      if (settings.id) {
        response = await adminV1OrganizationsPaymentSettingsUpdate(
          orgId,
          settings
        );
      } else {
        response = await adminV1OrganizationsPaymentSettingsCreate(
          orgId,
          settings
        );
      }

      paymentSettings.value = response;
    } catch (err) {
      console.error("Error saving payment settings:", err);
      error.value = "Failed to save payment settings";
      throw err;
    } finally {
      isSaving.value = false;
    }
  };

  return {
    paymentSettings: readonly(paymentSettings),
    isLoading: readonly(isLoading),
    isSaving: readonly(isSaving),
    error: readonly(error),
    fetchPaymentSettings,
    savePaymentSettings,
  };
};
